import React, { useState, useEffect } from "react";
import { pageCache } from '../utils/pageCache'
import { useParams, useNavigate } from "react-router-dom";
import AppBar from "../components/AppBar";
import LoadingSpinner from "../components/LoadingSpinner";
import StoreBox, { StoreBoxPersistedProps } from "../components/StoreBox";
import { bookmarkApi } from "../api/bookmark";
import { userApi } from "../api/user";
import { toastUtil } from "../utils/toast";

const SavedPlacesList: React.FC = () => {
  const navigate = useNavigate();
  const { userId, category } = useParams<{
    userId: string;
    category: string;
  }>();
  // 스와이프로 돌아왔을 때 이전 내용을 그대로 보여준다 (깜빡임 방지)
  const cacheKey = `saved-places-list:${userId ?? ''}:${category ?? ''}`
  const [places, setPlaces] = useState<StoreBoxPersistedProps[]>(
    () => pageCache.get<StoreBoxPersistedProps[]>(cacheKey) ?? []
  );
  const [isLoading, setIsLoading] = useState(!pageCache.has(cacheKey));

  useEffect(() => {
    if (!userId) {
      toastUtil.error("사용자 정보를 불러오는데 실패했습니다");
      navigate(-1);
      return;
    }

    const loadData = async () => {
      setIsLoading(true);
      try {
        // "우리 둘 다 저장한 곳" — 상대와의 저장 교집합 (가상 목록, 제거 불가)
        if (category === "common") {
          const res = await userApi.getCommonSavedPlaces(userId);
          setPlaces(
            res.places.map((p) => ({
              id: p.id,
              name: p.name,
              address: p.address,
              isBookmarked: p.isBookmarked,
              hasReview: Boolean(p.has_review),
              showRemoveButton: false,
            })),
          );
          return;
        }

        // 현재 사용자 ID 가져오기
        const currentUserResponse = await userApi.getProfile();
        const currentUserIdValue = currentUserResponse.user.id;

        // 해당 사용자의 저장된 장소 가져오기 (그 사용자가 직접 추가한 것만 — added_by_target)
        const response = await bookmarkApi.getBookmarks(userId, true);

        // 현재 사용자와 대상 사용자가 같은지 확인
        const isOwnList = currentUserIdValue === userId;

        // 모든 카테고리의 장소를 하나의 배열로 합치기
        // (백엔드가 category·has_review를 함께 내려주므로 장소별 추가 호출 불필요)
        const placeMap = new Map<
          string,
          StoreBoxPersistedProps & { placeCategory?: string; savedAt?: string }
        >();
        response.categories.forEach((cat) => {
          cat.items.forEach((item) => {
            const savedAt = item.saved_at ?? undefined;
            const prev = placeMap.get(item.id);
            if (!prev) {
              placeMap.set(item.id, {
                id: item.id,
                name: item.name,
                address: item.address,
                isBookmarked: item.isBookmarked,
                hasReview: Boolean(item.has_review),
                showRemoveButton: isOwnList, // 본인 리스트일 때만 제거 버튼 표시
                removeCategoryId: cat.id, // 제거 시 해당 카테고리에서 삭제
                placeCategory: item.category, // 카테고리 필터용
                savedAt,
              });
              return;
            }
            // 같은 장소가 여러 리스트에 있으면 가장 최근에 담은 시각을 쓴다.
            // (나머지 값은 처음 만난 리스트 기준을 유지 — 제거 버튼이 가리킬
            //  리스트가 도중에 바뀌면 안 되기 때문)
            if (savedAt && (!prev.savedAt || savedAt > prev.savedAt)) {
              prev.savedAt = savedAt;
            }
          });
        });

        // 카테고리 필터 (장소별 getPlace 호출 없이 바로 — 200개+ 리스트도 빠름)
        const categoryCode = category && category !== "all" ? category : null;
        const placesToShow = Array.from(placeMap.values())
          .filter((p) => !categoryCode || p.placeCategory === categoryCode)
          // 담은 순(최신이 위). 합치기만 하면 리스트 순서가 남아, 언제 담았는지보다
          // 어느 리스트에 있는지가 앞선다. 기본 저장함은 대개 가장 먼저 만들어져
          // 맨 뒤로 밀렸다.
          .sort((a, b) => (b.savedAt ?? "").localeCompare(a.savedAt ?? ""))
          .map(({ placeCategory, savedAt, ...rest }) => rest);
        pageCache.set(cacheKey, placesToShow);
        setPlaces(placesToShow);
      } catch (error: any) {
        const errorMessage =
          error.response?.data?.message ||
          "저장한 장소를 불러오는데 실패했습니다";
        toastUtil.error(errorMessage);
        navigate(-1);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [userId, category, navigate]);

  const handleRemove = (placeId: string) => {
    // 제거된 장소를 리스트에서 제거
    setPlaces((prevPlaces) =>
      prevPlaces.filter((place) => place.id !== placeId),
    );
  };

  // AppBar 타이틀 — 공통(교집합) 페이지 포함 항상 "저장한 장소"
  const getAppBarTitle = () => "저장한 장소";

  if (isLoading) {
    return (
      <div className="w-full h-full flex flex-col bg-backgroundColorBg2">
        <div className="app-container flex-1 flex flex-col overflow-hidden">
          <AppBar title={getAppBarTitle()} isBottomBorder />
          <div className="flex-1 flex items-center justify-center empty-state-center">
            <LoadingSpinner size={28} borderWidth={2} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full flex flex-col bg-backgroundColorBg2">
      <div className="app-container flex-1 flex flex-col overflow-hidden relative">
        {/* 헤더 */}
        <AppBar title={getAppBarTitle()} isBottomBorder />

        {/* 장소 목록 */}
        <div className="flex-1 overflow-y-auto scrollbar-hide relative">
          {places.length > 0 ? (
            <div className="w-full flex flex-col px-20px py-16px gap-24px">
              {places.map((place) => (
                <StoreBox
                  key={place.id}
                  {...place}
                  removeScope="all"
                  onRemove={
                    place.showRemoveButton
                      ? () => handleRemove(place.id)
                      : undefined
                  }
                  onCardClick={(id) => {
                    if (id) {
                      navigate(`/main-map?placeId=${id}`);
                    }
                  }}
                />
              ))}
            </div>
          ) : (
            <div className="absolute inset-0 flex flex-col items-center justify-center px-16px pointer-events-none empty-state-center">
              <p className="font-pretendard font-regular text-16px leading-180% text-textColorText3 text-center">
                {category === "common"
                  ? "우리 둘 다 저장한 장소가 없어요"
                  : "저장한 장소가 없습니다"}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SavedPlacesList;
